/**
 * Migration 2 — Lead Tracker.
 *
 * The per-unit lead workflow has three independent dimensions stored
 * on `unit_lead_status`: friction surfaces, exemption, and test result.
 * Each dimension has its own enum; `units.lead_status` (declared in
 * M1) is a *derived* rollup column computed from these three via
 * `deriveUnitLeadStatus`.
 *
 * Kept separate from the generic `constants.ts` so the lead surface
 * can grow its own constants (status labels, segmented-control option
 * lists, severity mapping) without bloating the shared root.
 */

import type { LeadStatus } from './constants.js';

// ---------- Friction surfaces ----------

export const FRICTION_STATUSES = ['not_addressed', 'clean'] as const;
export type FrictionStatus = (typeof FRICTION_STATUSES)[number];

export const FRICTION_STATUS_LABELS: Record<FrictionStatus, string> = {
  not_addressed: 'Not addressed',
  clean: 'Clean',
};

// ---------- Exemption ----------

export const EXEMPTION_STATUSES = [
  'none',
  'applied_pending',
  'approved',
  'rejected',
] as const;
export type ExemptionStatus = (typeof EXEMPTION_STATUSES)[number];

export const EXEMPTION_STATUS_LABELS: Record<ExemptionStatus, string> = {
  none: 'None',
  applied_pending: 'Applied (pending)',
  approved: 'Approved',
  rejected: 'Rejected',
};

// ---------- Test result ----------

export const TEST_RESULTS = ['not_tested', 'negative', 'positive'] as const;
export type TestResult = (typeof TEST_RESULTS)[number];

export const TEST_RESULT_LABELS: Record<TestResult, string> = {
  not_tested: 'Not tested',
  negative: 'Negative',
  positive: 'Positive',
};

// ---------- Derived status labels (display) ----------
//
// The eight canonical values are declared in `LEAD_STATUSES` in
// `constants.ts` (set up by Migration 1 so the column could exist).
// Labels live here next to the rest of the lead surface.

export const LEAD_STATUS_LABELS: Record<LeadStatus, string> = {
  untested: 'Untested',
  tested_negative: 'Tested negative',
  tested_positive: 'Tested positive',
  exempt_approved: 'Exempt — approved',
  exempt_pending: 'Exempt — pending',
  exempt_rejected: 'Exempt — rejected',
  friction_clean_only: 'Friction clean only',
  unknown: 'Unknown',
};

// ---------- Severity color mapping ----------
//
// Per DESIGN_SYSTEM §9 the severity palette is `critical | high |
// medium | low | info`. M2 §5 adds a `success` ribbon for the two
// "all good" lead statuses. We keep `success` as a string literal so
// callers can pattern-match against it without pulling the severity
// helper in here (which lives in `packages/ui`).

export type LeadStatusSeverity =
  | 'critical'
  | 'high'
  | 'medium'
  | 'low'
  | 'info'
  | 'success';

export const LEAD_STATUS_SEVERITY: Record<LeadStatus, LeadStatusSeverity> = {
  tested_positive: 'critical',
  exempt_rejected: 'high',
  friction_clean_only: 'medium',
  exempt_pending: 'medium',
  untested: 'low',
  tested_negative: 'success',
  exempt_approved: 'success',
  // M2 didn't list `unknown`; defensively map to `low` (gray) so
  // legacy rows surfaced via M1 still render somewhere on the
  // canonical scale instead of crashing the severity helper.
  unknown: 'low',
};

// ---------- Audit / event action names ----------
//
// Surfaced as constants so producers (services), subscribers (Phase 9
// notifications), and the activity-feed renderer all agree on the
// wire spelling.

export const LEAD_AUDIT_ACTIONS = {
  statusChanged: 'unit_lead.status_changed',
  workEventAdded: 'unit_lead.work_event_added',
  workEventEdited: 'unit_lead.work_event_edited',
  workEventDeleted: 'unit_lead.work_event_deleted',
  bulkFrictionClean: 'unit_lead.bulk_friction_clean',
} as const;

/** The fields tracked by the per-unit history table — must stay in
 *  lock-step with the columns persisted in `unit_lead_status_history`
 *  and with the diff computed in `upsertUnitLeadStatus`. */
export const LEAD_TRACKED_FIELDS = [
  'friction_status',
  'exemption_status',
  'exemption_letter_document_id',
  'test_result',
  'positive_count',
  'latest_xrf_date',
  'latest_xrf_report_document_id',
  'notes',
] as const;
export type LeadTrackedField = (typeof LEAD_TRACKED_FIELDS)[number];
